import React from 'react';
import Icon from '../../../components/AppIcon';

const UserTable = ({
  users,
  recruiters = [],
  selectedUsers,
  onUserSelect,
  onSelectAll,
  onEditUser,
  onChangeStatus,
  onDeleteUser,
  loading
}) => {
  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(date));
  };
  
  const getRoleKey = (role) => {
    if (typeof role === 'number') {
      const roleMap = { 0: 'admin', 1: 'clab', 2: 'recruiter' };
      return roleMap[role];
    }
    const roleStr = (role || '').toLowerCase();
    if (roleStr.includes('admin')) return 'admin';
    if (roleStr.includes('clab')) return 'clab';
    if (roleStr.includes('recruiter')) return 'recruiter';
    return 'unknown';
  };

  const getRoleDisplayName = (role) => {
    const roleKey = getRoleKey(role);
    const roleMap = {
      'admin': 'Master Admin',
      'clab': 'CLAB User',
      'recruiter': 'Recruiter'
    };
    return roleMap[roleKey] || 'Unknown Role';
  };

  const getRoleBadgeColor = (role) => {
    const roleKey = getRoleKey(role);
    const colorMap = {
      'admin': 'bg-error-100 text-error-800',
      'clab': 'bg-primary-100 text-primary-800',
      'recruiter': 'bg-accent-100 text-accent-800'
    };
    return colorMap[roleKey] || 'bg-secondary-100 text-secondary-800';
  };

  const getStatusBadge = (status) => {
    if (status.toString().toLowerCase() === 'active') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-800">
          <Icon name="CheckCircle" size={12} className="mr-1" />
          Active
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-error-100 text-error-800">
          <Icon name="XCircle" size={12} className="mr-1" />
          Inactive
        </span>
      );
    }
  };

  const isAllSelected = users.length > 0 && selectedUsers.length === users.length;
  const isIndeterminate = selectedUsers.length > 0 && selectedUsers.length < users.length;

  if (loading && users.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-secondary-200">
        <div className="p-12 text-center">
          <Icon name="Loader2" size={24} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-secondary-600">Loading users...</p>
        </div>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-secondary-200">
        <div className="p-12 text-center">
          <Icon name="Users" size={48} className="text-secondary-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-text-primary mb-2">No Users Found</h3>
          <p className="text-secondary-600">No users match your current filters.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-secondary-200 overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-secondary-50 border-b border-secondary-200">
            <tr>
              <th className="px-6 py-4 text-left">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = isIndeterminate;
                    }}
                    onChange={(e) => onSelectAll(e.target.checked)}
                    className="w-4 h-4 text-primary border-secondary-300 rounded focus:ring-primary-500"
                    disabled={loading}
                  />
                </div>
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                Last Login
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-secondary-200">
            {users.map((user) => {
              return (
                <tr key={user.id} className="hover:bg-secondary-50 transition-colors duration-150">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={(e) => onUserSelect(user.id, e.target.checked)}
                      className="w-4 h-4 text-primary border-secondary-300 rounded focus:ring-primary-500"
                      disabled={loading}
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-medium text-primary-800">
                          {user.full_name ? user.full_name.charAt(0).toUpperCase() : 'U'}
                        </span>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-text-primary">{user.full_name || 'Unknown'}</div>
                        <div className="text-sm text-secondary-600">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                      {user.role_name || getRoleDisplayName(user.role)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(user.status)}
                    {user.status === 'inactive' && user.remarks && (
                      <div className="text-xs text-secondary-600 mt-1">
                        {user.remarks}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-secondary-600">
                      {formatDate(user.last_login_at)}
                    </div>
                    <div className="text-xs text-secondary-500">
                      Created: {formatDate(user.created_at)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => onEditUser(user)}
                        className="p-2 text-secondary-600 hover:text-primary hover:bg-primary-50 rounded-md transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Edit user"
                      >
                        <Icon name="Edit" size={16} />
                      </button>
                      <button
                        onClick={() => onChangeStatus(user)}
                        className={`p-2 rounded-md transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed ${
                          user.status.toString().toLowerCase() === 'active' 
                            ? 'text-warning hover:text-warning-700 hover:bg-warning-50' 
                            : 'text-success hover:text-success-700 hover:bg-success-50'
                        }`}
                        title={user.status.toString().toLowerCase() === 'active' ? 'Change to inactive' : 'Change to active'}
                      >
                        <Icon name={user.status.toString().toLowerCase() === 'active' ? 'UserX' : 'UserCheck'} size={16} />
                      </button>
                      {/* <button
                        onClick={() => onDeleteUser(user)}
                        className="p-2 text-error hover:text-error-700 hover:bg-error-50 rounded-md transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Delete user"
                      >
                        <Icon name="Trash2" size={16} />
                      </button> */}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile/Tablet Cards */}
      <div className="lg:hidden">
        <div className="p-4 bg-secondary-50 border-b border-secondary-200">
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={isAllSelected}
              ref={(el) => {
                if (el) el.indeterminate = isIndeterminate;
              }}
              onChange={(e) => onSelectAll(e.target.checked)}
              className="w-4 h-4 text-primary border-secondary-300 rounded focus:ring-primary-500 mr-3"
              disabled={loading}
            />
            <span className="text-sm text-secondary-700 font-medium">Select All Users</span>
          </div>
        </div>
        
        <div className="divide-y divide-secondary-200">
          {users.map((user) => {
            return (
              <div key={user.id} className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={(e) => onUserSelect(user.id, e.target.checked)}
                      className="w-4 h-4 text-primary border-secondary-300 rounded focus:ring-primary-500"
                      disabled={loading}
                    />
                    <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-medium text-primary-800">
                        {user.full_name ? user.full_name.charAt(0).toUpperCase() : 'U'}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-medium text-text-primary">{user.full_name || 'Unknown'}</h4>
                      <p className="text-sm text-secondary-600">{user.email}</p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                    {user.role_name || getRoleDisplayName(user.role)}
                  </span>
                </div>
                
                <div className="mt-4 space-y-3 text-sm text-secondary-700">
                  <div className="flex justify-between">
                    <strong>Status:</strong>
                    {getStatusBadge(user.status)}
                  </div>
                  {user.status === 'inactive' && user.remarks && (
                    <div className="flex justify-between">
                      <strong>Remarks:</strong>
                      <span>{user.remarks}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <strong>Last Login:</strong>
                    <span>{formatDate(user.last_login_at)}</span>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-secondary-200 flex justify-end items-center space-x-2">
                  <button
                    onClick={() => onEditUser(user)}
                    className="p-2 text-secondary-600 hover:text-primary hover:bg-primary-50 rounded-md transition-colors duration-150"
                    title="Edit user"
                  >
                    <Icon name="Edit" size={16} />
                  </button>
                  <button
                    onClick={() => onChangeStatus(user)}
                    className={`p-2 rounded-md transition-colors duration-150 ${
                      user.status.toString().toLowerCase() === 'active' 
                        ? 'text-warning hover:text-warning-700 hover:bg-warning-50' 
                        : 'text-success hover:text-success-700 hover:bg-success-50'
                    }`}
                    title={user.status.toString().toLowerCase() === 'active' ? 'Change to inactive' : 'Change to active'}
                  >
                    <Icon name={user.status.toString().toLowerCase() === 'active' ? 'UserX' : 'UserCheck'} size={16} />
                  </button>
                  {/* <button
                    onClick={() => onDeleteUser(user)}
                    className="p-2 text-error hover:text-error-700 hover:bg-error-50 rounded-md transition-colors duration-150"
                    title="Delete user"
                  >
                    <Icon name="Trash2" size={16} />
                  </button> */}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default UserTable;